/**
 * Operator-facing labels for EmailJob delivery projection.
 * Internal EmailJob.deliveryState remains accepted ≠ delivered.
 */

export type SetupEmailOperatorStatus =
  | "not_sent"
  | "submitted"
  | "sent"
  | "delivered"
  | "failed"
  | "bounced";

/** Bounded live refresh while Client Details stays open. */
export const SETUP_EMAIL_POLL_INTERVAL_MS = 2500;
export const SETUP_EMAIL_MAX_POLLS = 20;
export const SETUP_EMAIL_POLL_WINDOW_MS =
  SETUP_EMAIL_POLL_INTERVAL_MS * SETUP_EMAIL_MAX_POLLS;

const TERMINAL_OPERATOR_STATUSES = new Set<SetupEmailOperatorStatus>([
  "delivered",
  "failed",
  "bounced",
]);

export function isSetupEmailTerminalStatus(
  status: SetupEmailOperatorStatus | string | null | undefined,
): boolean {
  return TERMINAL_OPERATOR_STATUSES.has(status as SetupEmailOperatorStatus);
}

export function shouldDismissSetupEmailSubmitNotice(
  status: SetupEmailOperatorStatus | string | null | undefined,
): boolean {
  return (
    status === "sent" ||
    status === "delivered" ||
    status === "failed" ||
    status === "bounced"
  );
}

/**
 * Continue bounded polling for in-flight delivery (Submitted/Sent),
 * including the brief window after submit before the first status fetch lands.
 */
export function shouldPollSetupEmailStatus(args: {
  status: SetupEmailOperatorStatus | string | null | undefined;
  hasTrackedJob: boolean;
  pollCount: number;
  maxPolls?: number;
}): boolean {
  const maxPolls = args.maxPolls ?? SETUP_EMAIL_MAX_POLLS;
  if (args.pollCount >= maxPolls) return false;
  if (isSetupEmailTerminalStatus(args.status)) return false;
  if (args.status === "not_sent" && !args.hasTrackedJob) return false;
  return true;
}

export function setupEmailStatusIndicatorKind(
  status: SetupEmailOperatorStatus | string | null | undefined,
): "none" | "in_progress" | "delivered" | "failed" | "bounced" {
  switch (status) {
    case "submitted":
    case "sent":
      return "in_progress";
    case "delivered":
      return "delivered";
    case "failed":
      return "failed";
    case "bounced":
      return "bounced";
    default:
      return "none";
  }
}

export function setupEmailStatusLabel(
  status: SetupEmailOperatorStatus | string | null | undefined,
): string {
  switch (status) {
    case "submitted":
      return "Submitted";
    case "sent":
      return "Sent";
    case "delivered":
      return "Delivered";
    case "failed":
      return "Failed";
    case "bounced":
      return "Bounced";
    case "not_sent":
    default:
      return "Not Sent";
  }
}

export function formatSetupEmailTimestamp(
  value: string | null | undefined,
): string {
  if (!value) return "—";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "—";
  return parsed.toLocaleString();
}
